"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import {
  LayoutDashboard,
  CarFront,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Car,
  type LucideIcon,
} from "lucide-react";
import Modal from "@/components/ui/Modal";

type PreviewSize = "compact" | "modal";

/** Мини-макет главной: как BannerCarousel — мок-тексты и «картинка» */
function PreviewHomeHero({ previewSize = "compact" }: { previewSize?: PreviewSize }) {
  const t = useTranslations("Home");
  const isModal = previewSize === "modal";
  return (
    <div
      className={`relative mx-auto w-full overflow-hidden rounded-lg bg-gray-950 shadow-lg ring-1 ring-white/10 ${
        isModal ? "max-w-none rounded-xl" : "max-w-[320px]"
      }`}
      style={{ aspectRatio: "16 / 7" }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a] via-[#1e1b4b] to-gray-950" />
      <div
        className="absolute inset-0 opacity-90"
        style={{
          background:
            "radial-gradient(ellipse 70% 80% at 88% 45%, rgba(230,57,70,0.22), transparent 55%), radial-gradient(ellipse 50% 40% at 20% 80%, rgba(56,189,248,0.12), transparent 50%)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/15 to-transparent" />

      <Car
        className={`pointer-events-none absolute top-1/2 z-[1] -translate-y-1/2 text-white ${isModal ? "-right-6 h-[115%] w-auto opacity-[0.09]" : "-right-3 h-[100%] w-auto opacity-[0.07]"}`}
        strokeWidth={1}
        aria-hidden
      />

      <div
        className={`absolute bottom-2 left-2 z-10 max-w-[min(72%,18rem)] pr-12 sm:pr-14 ${isModal ? "bottom-3 left-3 max-w-[min(82%,24rem)] pr-20 sm:pr-24" : ""}`}
      >
        <div className={`mb-1 flex items-center gap-1.5 ${isModal ? "mb-2 gap-2" : ""}`}>
          <span className={`shrink-0 rounded-full bg-[#E63946] ${isModal ? "h-0.5 w-5" : "h-px w-3"}`} />
          <span
            className={`font-semibold uppercase tracking-[0.12em] text-white/75 ${isModal ? "text-[10px] sm:text-[11px]" : "text-[6px] sm:text-[7px]"}`}
          >
            {t("adMockHomeSubtitle")}
          </span>
          <span className={`shrink-0 rounded-full bg-[#E63946] ${isModal ? "h-0.5 w-5" : "h-px w-3"}`} />
        </div>
        <h3
          className={`font-bold leading-tight tracking-tight text-white drop-shadow-md line-clamp-2 ${
            isModal ? "text-sm sm:text-lg md:text-xl" : "text-[9px] leading-snug sm:text-[10px]"
          }`}
          style={{ textShadow: "0 2px 16px rgba(0,0,0,0.5)" }}
        >
          {t("adMockHomeTitle")}
        </h3>
        <div
          className={`mt-1.5 inline-flex items-center gap-1 rounded-lg bg-white font-semibold text-gray-900 shadow-md ${
            isModal ? "px-3 py-1.5 text-xs sm:text-sm" : "px-1.5 py-0.5 text-[6px] sm:text-[7px]"
          }`}
        >
          {t("adMockHomeCta")}
          <ExternalLink className={isModal ? "size-3.5 shrink-0 opacity-70" : "size-2.5 shrink-0 opacity-70"} aria-hidden />
        </div>
      </div>

      <div
        className={`absolute right-2 top-1.5 z-10 rounded-full border border-white/15 bg-black/30 font-semibold uppercase tracking-widest text-white/55 backdrop-blur-sm ${
          isModal ? "px-2 py-1 text-[10px]" : "px-1.5 py-0.5 text-[6px]"
        }`}
      >
        {t("adMockLabelLower")}
      </div>
      <div className="absolute bottom-0 left-0 right-0 z-10 h-0.5 bg-white/10">
        <div className="h-full w-[38%] bg-[#E63946]" />
      </div>
      <div
        className={`absolute z-10 hidden overflow-hidden rounded-md border border-white/20 bg-black/40 sm:block ${
          isModal ? "right-3 bottom-4 h-9 w-[4.5rem]" : "right-2 bottom-3 h-5 w-8"
        }`}
      >
        <div className="h-full w-full bg-gradient-to-br from-sky-600/50 to-indigo-900/70" />
      </div>
    </div>
  );
}

/** Мини-макет каталога: как CatalogBanner */
function PreviewCatalogStrip({ previewSize = "compact" }: { previewSize?: PreviewSize }) {
  const t = useTranslations("Home");
  const isModal = previewSize === "modal";
  return (
    <div
      className={`relative mx-auto w-full overflow-hidden rounded-2xl bg-gray-900 shadow-lg ring-1 ring-white/10 ${
        isModal ? "h-32 max-w-none" : "h-[4.5rem] max-w-[300px]"
      }`}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-950/90 via-violet-950/80 to-slate-900" />
      <div
        className="absolute inset-y-0 right-0 w-[42%] bg-gradient-to-l from-cyan-500/25 via-blue-600/15 to-transparent"
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent" />

      <div
        className={`absolute top-1/2 z-[1] -translate-y-1/2 text-white/10 ${isModal ? "right-[10%] h-16 w-16" : "right-[8%] h-10 w-10"}`}
        aria-hidden
      >
        <Car className="h-full w-full" strokeWidth={1} />
      </div>

      <div
        className={`absolute inset-y-0 left-0 z-10 flex flex-col justify-center ${isModal ? "right-[38%] pl-4 pr-2" : "right-[36%] pl-3 pr-2"}`}
      >
        <p
          className={`font-bold leading-snug text-white drop-shadow line-clamp-1 ${
            isModal ? "text-sm sm:text-base" : "text-[11px] sm:text-xs"
          }`}
        >
          {t("adMockCatalogTitle")}
        </p>
        <p
          className={`mt-0.5 line-clamp-1 text-white/80 drop-shadow ${isModal ? "text-xs sm:text-sm" : "text-[9px] sm:text-[10px]"}`}
        >
          {t("adMockCatalogSubtitle")}
        </p>
      </div>

      <div
        className={`absolute right-2 top-1.5 z-10 rounded-full bg-black/40 font-medium text-white/75 backdrop-blur-sm ${
          isModal ? "px-2 py-1 text-[10px]" : "px-1.5 py-0.5 text-[6px]"
        }`}
      >
        {t("adMockLabel")}
      </div>
      <div className="absolute bottom-1.5 right-2 z-10 flex gap-0.5">
        <span className={`rounded-full bg-white ${isModal ? "h-1.5 w-5" : "h-1 w-3"}`} />
        <span className={`rounded-full bg-white/45 ${isModal ? "h-1.5 w-1.5" : "h-1 w-1"}`} />
        <span className={`rounded-full bg-white/45 ${isModal ? "h-1.5 w-1.5" : "h-1 w-1"}`} />
      </div>
      <span
        className={`pointer-events-none absolute left-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white ${
          isModal ? "h-8 w-8" : "h-5 w-5"
        }`}
        aria-hidden
      >
        <ChevronLeft className={isModal ? "size-4" : "size-3"} />
      </span>
      <span
        className={`pointer-events-none absolute top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white ${
          isModal ? "right-10 h-8 w-8" : "right-8 h-5 w-5"
        }`}
        aria-hidden
      >
        <ChevronRight className={isModal ? "size-4" : "size-3"} />
      </span>
    </div>
  );
}

/** Мини-макет страницы авто: объявление + баннер */
function PreviewCarPageBanner({ previewSize = "compact" }: { previewSize?: PreviewSize }) {
  const t = useTranslations("Home");
  const isModal = previewSize === "modal";
  return (
    <div
      className={`mx-auto w-full rounded-xl border border-white/10 bg-gray-950/90 p-2 shadow-lg ring-1 ring-white/5 ${
        isModal ? "max-w-none p-3" : "max-w-[300px]"
      }`}
    >
      <div className="flex gap-2.5">
        <div
          className={`relative shrink-0 overflow-hidden rounded-lg ring-1 ring-white/10 ${
            isModal ? "h-[4.5rem] w-[28%] min-w-[3.5rem] sm:h-24 sm:w-[26%]" : "h-14 w-[24%] min-w-[3rem]"
          }`}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-600 via-slate-800 to-gray-950" />
          <div
            className="absolute inset-0 opacity-80"
            style={{
              background:
                "linear-gradient(135deg, rgba(230,57,70,0.15) 0%, transparent 45%), linear-gradient(to top, rgba(0,0,0,0.5), transparent 60%)",
            }}
          />
          <Car className="absolute left-1/2 top-1/2 h-[55%] w-[55%] -translate-x-1/2 -translate-y-1/2 text-white/20" strokeWidth={1.2} aria-hidden />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <p
            className={`truncate font-semibold leading-tight text-white ${isModal ? "text-sm sm:text-base" : "text-[11px] sm:text-xs"}`}
          >
            {t("adMockCarListingTitle")}
          </p>
          <p className={`mt-0.5 truncate text-white/45 ${isModal ? "text-xs" : "text-[9px] sm:text-[10px]"}`}>
            {t("adMockCarListingMeta")}
          </p>
          <p className={`mt-1 font-bold text-[#E63946] ${isModal ? "text-base sm:text-lg" : "text-xs sm:text-sm"}`}>
            {t("adMockCarListingPrice")}
            <span className={`ml-0.5 font-normal text-white/35 ${isModal ? "text-xs" : "text-[9px]"}`}>
              {t("adMockCarListingPerDay")}
            </span>
          </p>
        </div>
      </div>

      <div className={`relative mt-2.5 overflow-hidden rounded-xl ring-1 ring-white/10 ${isModal ? "h-[4.25rem] sm:h-20" : "h-11"}`}>
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/90 via-teal-950/70 to-slate-900" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 to-transparent" />
        <div
          className={`absolute inset-y-0 left-0 z-[1] flex flex-col justify-center ${isModal ? "right-[32%] px-3" : "right-[30%] px-2.5"}`}
        >
          <p
            className={`font-bold leading-tight text-white drop-shadow line-clamp-1 ${
              isModal ? "text-xs sm:text-sm" : "text-[10px] sm:text-[11px]"
            }`}
          >
            {t("adMockCarBannerTitle")}
          </p>
          <p className={`mt-0.5 line-clamp-1 text-white/75 ${isModal ? "text-[11px] sm:text-xs" : "text-[8px] sm:text-[9px]"}`}>
            {t("adMockCarBannerSubtitle")}
          </p>
        </div>
        <div
          className={`absolute right-2 top-1 z-10 rounded-full bg-black/40 font-medium text-white/70 ${
            isModal ? "px-1.5 py-0.5 text-[8px]" : "px-1 py-px text-[5px] sm:text-[6px]"
          }`}
        >
          {t("adMockLabel")}
        </div>
      </div>
    </div>
  );
}

export type AdPlacementVariant = "home" | "catalog" | "car";

export type AdPlacementCardItem = {
  variant: AdPlacementVariant;
  label: string;
  description: string;
  previewHint: string;
};

const PLACEMENT_ICON: Record<AdPlacementVariant, { Icon: LucideIcon; accent: string; iconBg: string }> = {
  home: { Icon: LayoutDashboard, accent: "text-sky-300", iconBg: "bg-sky-400/15" },
  catalog: { Icon: CarFront, accent: "text-fuchsia-300", iconBg: "bg-fuchsia-400/15" },
  car: { Icon: BookOpen, accent: "text-emerald-300", iconBg: "bg-emerald-400/15" },
};

function PreviewByVariant({
  variant,
  previewSize = "compact",
}: {
  variant: AdPlacementVariant;
  previewSize?: PreviewSize;
}) {
  switch (variant) {
    case "home":
      return <PreviewHomeHero previewSize={previewSize} />;
    case "catalog":
      return <PreviewCatalogStrip previewSize={previewSize} />;
    default:
      return <PreviewCarPageBanner previewSize={previewSize} />;
  }
}

export default function AdPlacementCards({
  items,
  openPreviewLabel,
}: {
  items: AdPlacementCardItem[];
  openPreviewLabel: string;
}) {
  const [open, setOpen] = useState<AdPlacementVariant | null>(null);
  const active = open ? items.find((i) => i.variant === open) : undefined;

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
        {items.map(({ label, description, variant }) => {
          const { Icon, accent, iconBg } = PLACEMENT_ICON[variant];
          return (
            <button
              key={variant}
              type="button"
              onClick={() => setOpen(variant)}
              className="group/card rounded-2xl border border-white/12 bg-white/[0.04] p-4 text-left outline-none transition-all hover:border-white/25 hover:bg-white/[0.08] focus-visible:border-white/25 focus-visible:bg-white/[0.08] focus-visible:ring-2 focus-visible:ring-white/20"
            >
              <div className="flex items-start gap-3">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconBg} ${accent}`}>
                  <Icon size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="mb-1 text-sm font-semibold text-white">{label}</p>
                  <p className="text-xs leading-relaxed text-gray-300">{description}</p>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-1 text-xs font-medium text-white/50 transition-colors group-hover/card:text-white/75">
                <span>{openPreviewLabel}</span>
                <ChevronRight size={14} className="shrink-0 opacity-70" aria-hidden />
              </div>
            </button>
          );
        })}
      </div>

      <Modal
        open={!!open}
        onClose={() => setOpen(null)}
        title={active?.label ?? ""}
        size="xl"
        closeOnBackdrop
      >
        {active && (
          <div className="space-y-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{active.previewHint}</p>
            <p className="text-sm leading-relaxed text-gray-500 dark:text-gray-400">{active.description}</p>
            <div className="rounded-xl border border-gray-200 bg-gray-950 p-4 sm:p-6 dark:border-gray-700">
              <PreviewByVariant variant={active.variant} previewSize="modal" />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

/** Карточки рекламных мест для главной: строки и иконки только на клиенте (без пропсов с RSC). */
export function AdPlacementCardsHome() {
  const t = useTranslations("Home");
  const items = useMemo(
    (): AdPlacementCardItem[] => [
      {
        variant: "home",
        label: t("adCardHome"),
        description: t("adCardHomeDesc"),
        previewHint: t("adCardPreviewHintHome"),
      },
      {
        variant: "catalog",
        label: t("adCardCatalog"),
        description: t("adCardCatalogDesc"),
        previewHint: t("adCardPreviewHintCatalog"),
      },
      {
        variant: "car",
        label: t("adCardCar"),
        description: t("adCardCarDesc"),
        previewHint: t("adCardPreviewHintCar"),
      },
    ],
    [t],
  );
  return <AdPlacementCards items={items} openPreviewLabel={t("adCardOpenPreview")} />;
}
